import {httpsCallable} from 'firebase/functions';
import {useCallback, useMemo} from 'react';
import {useFunctions} from 'reactfire';
import {LogFunction, LogLevel, LogPayload, LoggerLike} from '#common/logging/logger';

export const LogToCloudFunctionName = 'logToCloud';

/**
 * Serialized representation of an Error object for transmission to cloud logging.
 */
export interface SerializedError {
  cause?: SerializedError;
  message: string;
  name: string;
  stack?: string;
}

/**
 * Serializes an Error object to a plain object that can be JSON stringified.
 * Error properties (message, name, stack) are non-enumerable by default,
 * so they need to be explicitly extracted.
 */
function serializeError(error: Error): SerializedError {
  return {
    cause: error.cause instanceof Error ? serializeError(error.cause) : undefined,
    message: error.message,
    name: error.name,
    stack: error.stack,
  };
}

/**
 * Serializes a value for transmission to cloud logging.
 * Handles Error objects specially since their properties are non-enumerable.
 */
function serializeForLogging(value: unknown): unknown {
  if (value instanceof Error) {
    return serializeError(value);
  }
  return value;
}

export function useCloudLogger(): LoggerLike {
  const functions = useFunctions();

  const logToCloudFunction = useMemo(
    () => httpsCallable<LogPayload, void>(functions, LogToCloudFunctionName),
    [functions],
  );

  const log: LogFunction = useCallback(
    (level: LogLevel, message: string, ...optionalParams: unknown[]) => {
      const serializedParams = optionalParams.map(serializeForLogging);
      logToCloudFunction({level, message, optionalParams: serializedParams}).catch(() => {
        // Silently ignore logging failures to prevent disrupting the app
      });
    },
    [logToCloudFunction],
  );

  return useMemo(
    () => ({
      debug: (message: string, ...optionalParams: unknown[]) => log('debug', message, ...optionalParams),
      error: (message: string, ...optionalParams: unknown[]) => log('error', message, ...optionalParams),
      info: (message: string, ...optionalParams: unknown[]) => log('info', message, ...optionalParams),
      log,
      warn: (message: string, ...optionalParams: unknown[]) => log('warn', message, ...optionalParams),
    }),
    [log],
  );
}
